// Contrato del bloque COHORTS de `loan-offerings-business`, escrito a mano a partir de su
// `openapi/public.yaml`. Un cohort son condiciones unidas con Y sobre los datos del cliente.

export type CohortStatusWire = 'ACTIVE' | 'ARCHIVED';

/** Llaves del catálogo cerrado de datos del cliente. */
export type CohortFactKey =
    | 'sciLevel'
    | 'totalCredits'
    | 'activeCredits'
    | 'completedCredits'
    | 'lifetimeWriteOffs'
    | 'currentArrearsDays'
    | 'delinquencyDaysLast6M'
    | 'lifetimeRestructures'
    | 'isEmployee'
    | 'hasPcfAccount';

export type CohortFactType = 'NUMBER' | 'SELECT' | 'BOOLEAN';

export type CohortOperator =
    'EQUALS' | 'NOT_EQUALS' | 'GT' | 'GTE' | 'LT' | 'LTE' | 'IN' | 'NOT_IN';

/** MISSING = el cliente no tiene el dato. */
export type CohortConditionOutcome = 'MATCH' | 'NO_MATCH' | 'MISSING';

/** Eventos de la bitácora de cohorts y de segmentos. */
export type DefinitionLogEvent = 'CREATED' | 'UPDATED' | 'ARCHIVED' | 'REACTIVATED';

/** Número, true/false o una opción, según el tipo del dato. */
export type CohortFactValue = number | boolean | string;

/** Datos del cliente que se evaluaron. Un dato ausente no viene. */
export type CustomerFacts = Partial<Record<CohortFactKey, CohortFactValue>>;

/** «dato operador valor». `values` solo con IN y NOT_IN; `value` con los demás. Nunca los dos. */
export interface CohortConditionRequest {
    field: CohortFactKey;
    op: CohortOperator;
    value?: CohortFactValue;
    values?: string[];
}

export interface CreateCohortRequest {
    name: string;
    description?: string;
    /** De 1 a 10, unidas con Y. */
    conditions: CohortConditionRequest[];
}

/** Parche parcial: omitir una clave la deja como estaba. `conditions` reemplaza la lista entera. */
export interface UpdateCohortRequest {
    name?: string;
    description?: string | null;
    conditions?: CohortConditionRequest[];
    /** Motivo del cambio; queda en la bitácora. */
    reason?: string;
}

export interface ChangeCohortStatusRequest {
    status: CohortStatusWire;
    reason?: string;
}

/** Las condiciones pueden no estar guardadas. */
export interface TestCohortRequest {
    retailCustomerId: string;
    conditions: CohortConditionRequest[];
}

/** Condición guardada: `value` o `values` van en `null` cuando el operador no los usa. */
export interface CohortConditionResponse {
    field: CohortFactKey;
    op: CohortOperator;
    value: CohortFactValue | null;
    values: string[] | null;
}

export interface CohortSegmentRef {
    segmentId: string;
    name: string;
}

export interface CohortResponse {
    cohortId: string;
    name: string;
    description: string | null;
    conditions: CohortConditionResponse[];
    status: CohortStatusWire;
    version: number;
    /** Clientes del último recálculo. `null` = sin calcular. */
    memberCount: number | null;
    /** Porcentaje de los acreditados del tenant, de 0 a 100. `null` = sin calcular. */
    pctTotal: number | null;
    countedAt: string | null;
    /** Nunca se contó o se editó después del último recálculo. */
    countPending: boolean;
    /** Segmentos ACTIVE que lo usan. */
    usedBySegments: CohortSegmentRef[];
    createdBy: string;
    updatedBy: string;
    createdAt: string;
    updatedAt: string;
}

export interface CohortListResponse {
    cohorts: CohortResponse[];
}

export interface CohortFactOption {
    value: string;
    label: string;
}

export interface CohortFactFieldResponse {
    key: CohortFactKey;
    type: CohortFactType;
    label: string;
    unit: string | null;
    /** Solo en SELECT. */
    options: CohortFactOption[] | null;
    operators: CohortOperator[];
    /** `false` = ningún sistema produce el dato: no se puede guardar un cohort que lo use. */
    hasSource: boolean;
    missingSourceReason: string | null;
}

export interface CohortCatalogResponse {
    fields: CohortFactFieldResponse[];
}

export interface CohortConditionTrace extends CohortConditionResponse {
    /** Valor del cliente. `null` = no tiene el dato. */
    actual: CohortFactValue | null;
    outcome: CohortConditionOutcome;
}

export interface TestCohortResponse {
    retailCustomerId: string;
    /** `true` si cumple todas las condiciones. */
    matched: boolean;
    facts: CustomerFacts;
    conditions: CohortConditionTrace[];
}

export interface CohortLogEntryResponse {
    cohortId: string;
    cohortName: string;
    event: DefinitionLogEvent;
    occurredAt: string;
    /** Tal como quedó asentado. */
    performedBy: string;
    reason: string | null;
    version: number;
}

/** Página de bitácora, del más reciente al más viejo. `nextCursor: null` = no hay más. */
export interface CohortLogPageResponse {
    entries: CohortLogEntryResponse[];
    nextCursor: string | null;
}

export interface CohortLogQuery {
    limit?: number;
    cursor?: string;
}

/** El feed del día: `day` es `YYYY-MM-DD` en hora de Ciudad de México; sin él, el día en curso. */
export interface CohortLogFeedQuery extends CohortLogQuery {
    day?: string;
}

/** `data` de un 409 `COHORT_IN_USE`: los segmentos de los que hay que quitarlo. */
export interface CohortInUseErrorData {
    cohortId: string;
    segments: CohortSegmentRef[];
}

/** `data` de un 422 `COHORT_RULE_INVALID`: qué condición corregir. */
export interface CohortRuleInvalidErrorData {
    problems: string[];
}

/** `data` de un 422 `COHORT_FIELD_WITHOUT_SOURCE`: las condiciones que usan un dato sin fuente. */
export interface CohortFieldWithoutSourceErrorData {
    fields: { index: number; field: CohortFactKey }[];
}

/** Los `code` del bloque de cohorts. Cada uno tiene su mensaje en pantalla (`cohorts/logic.ts`). */
export const COHORT_ERROR_CODES = [
    'COHORT_NOT_FOUND',
    'COHORT_VERSION_CONFLICT',
    'COHORT_NAME_TAKEN',
    'COHORT_IN_USE',
    'COHORT_RULE_INVALID',
    'COHORT_FIELD_WITHOUT_SOURCE',
    'COHORT_EVALUATOR_UNAVAILABLE',
    'LOAN_CREDIT_UNAVAILABLE',
] as const;

export type CohortErrorCode = (typeof COHORT_ERROR_CODES)[number];
